import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import sessionStore from 'client/Session/SessionStore';
import {Link} from 'react-router';
import {getModelForResourceType} from 'resources/modelRegistry';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {JSONLD as CourseStructuredData} from 'client/StructuredData/Course';

import {Text} from '@albert-io/atomic';

import './course-card.scss';

const CourseCard = ({as = 'div', children, className, subject, ...rest}) => {
  const WrapperComponent = as;
  return (
    <WrapperComponent className={classnames('course-card', className)} {...rest}>
      {children}
      {!sessionStore.hasValidSession() ? <CourseStructuredData subject={subject} /> : null}
    </WrapperComponent>
  );
};

CourseCard.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  children: PropTypes.node,
  className: PropTypes.string,
  subject: PropTypes.instanceOf(getModelForResourceType('subject_v2')),
  to: PropTypes.string
};

CourseCard.Logo = ({className, color, src}) => {
  return (
    <div
      className={classnames('course-card__icon-wrapper', 'u-bgc_blue-400', className)}
      style={color && {backgroundColor: color}}
    >
      <img className='course-card__icon' src={src} alt='' />
    </div>
  );
};

CourseCard.Logo.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  src: PropTypes.string
};

CourseCard.Body = ({children, className}) => {
  return <div className={classnames('u-pad-r_1', className)}>{children}</div>;
};

CourseCard.Body.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

CourseCard.BodyContent = ({children}) => {
  return (
    <Text className='course-card__description' emphasis='lowest' size='s'>
      {typeof children === 'string' ? <MarkdownRendererV2 text={children} /> : children}
    </Text>
  );
};

CourseCard.BodyContent.propTypes = {
  children: PropTypes.node
};

CourseCard.Title = ({children, className, ...rest}) => {
  return (
    <Text as='h5' className={classnames('course-card__title', className)} bold {...rest}>
      {children}
    </Text>
  );
};

CourseCard.Title.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

CourseCard.ActionsWrapper = ({children}) => {
  return <div className='u-mar-t_1 u-mar-r_1 u-mar-l_auto'>{children}</div>;
};

CourseCard.ActionsWrapper.propTypes = {
  children: PropTypes.node
};

const BasicCourseCard = ({content, subject, className, to, disabled}) => {
  return (
    <CourseCard
      className={classnames('course-card', className, {'course-card--disabled': disabled})}
      onClick={(e) => e.currentTarget.getElementsByTagName('a')[0]?.click()}
      subject={subject}
    >
      <CourseCard.Logo
        color={
          !subject.getCurriculumArea().isEmpty() ? subject.getCurriculumArea().getColor() : null
        }
        className='course-card__icon-wrapper'
        src={subject.getLogoLocation().href}
      />
      <CourseCard.Body
        className={classnames({
          'u-display_flex u-flex-direction_column u-justify-content_center': !content,
          'u-mar-t_0 u-mar-lr_0 u-mar-b_auto': content
        })}
      >
        <CourseCard.Title className={classnames({'course-card__title': content})}>
          <Link to={to}>{subject.getName()}</Link>
        </CourseCard.Title>
        {content ? <CourseCard.BodyContent>{content}</CourseCard.BodyContent> : null}
      </CourseCard.Body>
    </CourseCard>
  );
};

BasicCourseCard.propTypes = {
  className: PropTypes.string,
  content: PropTypes.node,
  disabled: PropTypes.bool,
  subject: PropTypes.instanceOf(getModelForResourceType('subject_v2')),
  to: PropTypes.string
};

export {BasicCourseCard, CourseCard};
